import type { ApiFailure } from "./errors";

/**
 * Diffusion des échecs d'API.
 * ===========================
 *
 * ── Le problème ─────────────────────────────────────────────────────────────
 * Certains codes d'erreur ne concernent pas l'appel qui les a produits, mais **l'état de la
 * session**. `EMAIL_NOT_VERIFIED` en est l'exemple : il peut tomber sur n'importe quel
 * endpoint, et la réponse attendue est la même partout — inviter à vérifier son email.
 *
 * Sans point central, chaque couche `data/` de chaque feature devrait guetter ce code.
 * Autant dire qu'une feature l'oublierait, et que l'interface mentirait à cet endroit-là.
 *
 * ── Ce que fait ce module, et ce qu'il ne fait pas ──────────────────────────
 * Il **diffuse** les échecs, sans rien interpréter : `core` ne connaît aucun code métier.
 * C'est la feature concernée qui s'abonne et décide — `feature-auth` pour
 * `EMAIL_NOT_VERIFIED`. Le jour où elle est retirée par le prune, plus personne n'écoute,
 * et le socle n'a pas à le savoir.
 *
 * Utile aussi pour brancher de l'observabilité (journal, compteur) sans toucher aux écrans.
 */

type FailureListener = (failure: ApiFailure) => void;

const listeners = new Set<FailureListener>();

/**
 * S'abonner à tous les échecs d'API. Renvoie la fonction de désabonnement.
 *
 *   const stop = observeApiFailures((f) => {
 *     if (f.code === "EMAIL_NOT_VERIFIED") banner.show();
 *   });
 */
export function observeApiFailures(listener: FailureListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Appelé par `attempt()` à chaque échec. Interne au socle.
 *
 * Un abonné qui lève ne doit pas empêcher les autres d'être notifiés, ni faire échouer
 * l'appel d'origine : une erreur dans l'observateur serait un comble.
 */
export function notifyApiFailure(failure: ApiFailure): void {
  for (const listener of listeners) {
    try {
      listener(failure);
    } catch (cause) {
      console.error("Un observateur d'échec d'API a levé une exception", cause);
    }
  }
}

/** Réservé aux tests unitaires, pour repartir d'un état propre. */
export function resetApiFailureObservers(): void {
  listeners.clear();
}
